import { TableView, TableHeader, Column, TableBody, Row, Cell, View, Flex, ActionButton, DialogContainer, Dialog, Content, Heading } from '@adobe/react-spectrum';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './product_detail_view.module.css';
import { Plus } from 'react-bootstrap-icons';
import ProductComponentLineAddEdit from './formComponents.tsx/ProductComponentLineAddEdit';
import { useViewInfo } from '../../../../hooks/UseViewInfo';
import { useTranslation } from 'react-i18next';

export interface ComponentRowData {
  amount: string
  amountWithVat: string
  component: string
  componentCategory: string
  componentCategoryName: string
  componentName: string
  componentType: string
  countMax: string
  countMin: string
  currency: string
  currencyName: string
  isParent: string
  priceInclude: string
  purchasePrice: string
  ratio: string
  vatName: string
  vatValue: string
  note: string
}

const ComponentRelations = ({ data, onChange, formData, expandedSectionKey }: any) => {
  let defaultRowData: ComponentRowData = {
    amount: '',
    amountWithVat: '',
    component: '',
    componentCategory: '',
    componentCategoryName: '',
    componentName: '',
    componentType: '',
    countMax: '',
    countMin: '',
    currency: '',
    currencyName: '',
    isParent: '',
    priceInclude: '',
    purchasePrice: '',
    ratio: '',
    vatName: '',
    vatValue: '',
    note: '',
  };
  
  const { t } = useTranslation('products');
  const _t = (key: string) => t(`table_columns.${key}`, { ns: 'products' });
  const _tt = (key: string) => t(`product_common.${key}`, { ns: 'products' });

  const items = useMemo(() => data ?? [], [data]);
  const [selectedRow, setSelectedRow] = useState<ComponentRowData>(defaultRowData);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { isMobile } = useViewInfo();
  const componentColumns = [
    { name: _t('product_name'), key: 'componentName' },
    { name: _t('category_name'), key: 'componentCategoryName' },
    { name: _t('purchase_price'), key: 'purchasePrice' },
    { name: _t('ratio'), key: 'ratio' },
    { name: _t('min'), key: 'countMin' },
    { name: _t('max'), key: 'countMax' },
    { name: _t('note'), key: 'note' },
  ];

  const buttonRef = useRef<any>(null);
  useEffect(() => {
    if (expandedSectionKey?.has?.("componentSection")) {
      setTimeout(() => {
        buttonRef.current?.focus();
      }, 200);
    }
  }, [expandedSectionKey]);

  function handleRowClose(): void {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    (isMobile || window.innerWidth < 1030) && setIsOpen(false);
    handleUnselect();
  }

  function addRecord(record: any): void {
    const updatedComponents = [...items, record];
    onChange({ ...formData, COMPONENT: updatedComponents });
  }

  function updateRecord(record: any): void {
    const updatedComponents = items.map((item: any) =>
      item.component === record.component ? record : item
    );
    onChange({ ...formData, COMPONENT: updatedComponents });
  }

  function deleteRecord(record: any): void {
    const updatedComponents = items.filter((item: any) => item.component !== record.component);
    onChange({ ...formData, COMPONENT: updatedComponents });
    handleRowClose();
  }

  function closeDialog(isOpen: boolean): void {
    setIsOpen(isOpen);
    setShowAddEditRow(isOpen);
  }

  const handleRowClick = (rowId: any): void => {
    const rowData: any = data.find((row: any) => row.component === rowId.currentKey) || {};
    setSelectedRow(rowData);
    setShowAddEditRow(true);
    (isMobile || window.innerWidth < 1030) && setIsOpen(true);
    setSelectedKeys(rowId);
  };

  const handleUnselect = () => {
    setSelectedKeys(new Set());
  };

  return (
    <>
      <View marginTop={30} UNSAFE_className={styles.greybox_parent}>
        <Flex justifyContent="space-between" alignItems="center" UNSAFE_style={{ width: '100%' }}>
          <View UNSAFE_className={styles.value_header}><b>{_tt('components')}</b></View>
          <Flex gap="size-100">
            <ActionButton
              onPress={e => {
                setSelectedRow(defaultRowData);
                handleUnselect();
                (isMobile || window.innerWidth < 1030) && setIsOpen(true);
                setShowAddEditRow(true);
              }}
              isQuiet aria-label="Add"
              ref={buttonRef}>
              <View UNSAFE_className={styles.add_component}><Plus size={20} /> {_tt('button_add_component')}</View>
            </ActionButton>
          </Flex>
        </Flex>
      </View>
      <TableView
        UNSAFE_className="tblLayoutComponent"
        aria-label="Component Table"
        onSelectionChange={handleRowClick}
        selectionMode="single"
        selectedKeys={selectedKeys}
        selectionStyle="highlight"
        width={{ base: '100%', L: '100%', M: '100%' }}
        minHeight={'80px'}
        maxHeight={{ base: '1000px', L: '450px' }}
        marginBottom={'size-250'}>
        <TableHeader columns={componentColumns}>
          {(column: any) => (
            <Column showDivider key={column.key}>
              {column.name}
            </Column>
          )}
        </TableHeader>

        <TableBody items={items}>
          {(item: any) => (
            <Row key={item.component}>
              {(columnKey) => (
                <Cell>
                  {item[columnKey] ?? '-'}
                </Cell>
              )}
            </Row>
          )}
        </TableBody>
      </TableView>
      {(isMobile || window.innerWidth < 1030) ? (
        <DialogContainer
          isDismissable
          onDismiss={() => {
            setIsOpen(false);
          }}
        >
          {isOpen && (
            <Dialog>
              <Heading>
                {selectedRow.component === '' ?
                  "Add" :
                  "Edit"}
              </Heading>
              <Content>
                <ProductComponentLineAddEdit
                  id={String(defaultRowData?.component)}
                  defaultRowData={defaultRowData}
                  selectedRowData={selectedRow}
                  handleRowClose={handleRowClose}
                  addRecord={addRecord}
                  updateRecord={updateRecord}
                  deleteRecord={deleteRecord}
                  closeDialog={closeDialog}
                  styles={styles}
                />
              </Content>
            </Dialog>
          )}
        </DialogContainer>
      ) : (
        showAddEditRow && (
          <ProductComponentLineAddEdit
            id={String(defaultRowData?.component)}
            defaultRowData={defaultRowData}
            selectedRowData={selectedRow}
            handleRowClose={handleRowClose}
            addRecord={addRecord}
            updateRecord={updateRecord}
            deleteRecord={deleteRecord}
            closeDialog={closeDialog}
            styles={styles}
          />
        )
      )
      }
    </>

  );
};

export default ComponentRelations;
